<template>
  <div class="card">
    <div
      class="card-header d-flex align-items-center justify-content-between flex-wrap"
      style="gap: 8px"
    >
      <span class="card-title mb-0">{{ $t('teamType.title') }}</span>
      <b-form-checkbox
        v-if="hasChildren"
        v-model="incluirSubnos"
        switch
        class="ml-auto mr-50"
        @change="carregar"
      >
        {{ $t('team.list.includeSubNodes') }}
      </b-form-checkbox>
      <b-button
        v-if="$hasAbility('equipe.gerenciar')"
        size="sm"
        variant="primary"
        @click="abrirNovo"
      >
        <feather-icon
          icon="PlusIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('teamType.list.newButton') }}
      </b-button>
    </div>

    <div
      v-if="carregando"
      class="text-center py-4"
    >
      <b-spinner
        small
        variant="primary"
      />
    </div>

    <div
      v-else-if="tipos.length === 0"
      class="text-center text-muted py-4 px-2"
    >
      {{ $t('teamType.list.empty') }}
    </div>

    <div
      v-else
      class="table-responsive"
    >
      <table class="table table-sm table-hover mb-0">
        <thead>
          <tr>
            <th>{{ $t('teamType.table.name') }}</th>
            <th v-if="mostrarNo">
              {{ $t('team.list.nodeColumn') }}
            </th>
            <th>{{ $t('common.status') }}</th>
            <th class="text-right">
              {{ $t('common.actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tipo in tipos"
            :key="tipo.id"
          >
            <td>
              {{ tipo.name }}
              <small
                v-if="tipo.description"
                class="d-block text-muted"
              >{{ tipo.description }}</small>
            </td>
            <td v-if="mostrarNo">
              <span :class="{ 'text-muted': Number(tipo.node_id) === Number(nodeId) }">
                {{ tipo.node ? tipo.node.name : '—' }}
              </span>
            </td>
            <td>
              <b-badge :variant="Number(tipo.status) === 1 ? 'light-success' : 'light-secondary'">
                {{ Number(tipo.status) === 1 ? $t('common.active') : $t('common.inactive') }}
              </b-badge>
            </td>
            <td
              class="text-right"
              style="white-space: nowrap"
            >
              <template v-if="podeGerenciar(tipo)">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="mr-50"
                  :title="$t('common.edit')"
                  @click="abrirEdicao(tipo)"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="13"
                  />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  :title="$t('common.delete')"
                  @click="excluir(tipo)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="13"
                  />
                </b-button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-modal
      v-model="mostrarForm"
      :title="form.id ? $t('teamType.form.titleEdit') : $t('teamType.form.titleNew')"
      hide-footer
    >
      <div class="form-group">
        <label>
          <span class="required">*</span>
          {{ $t('teamType.form.nameLabel') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          maxlength="100"
          :placeholder="$t('teamType.form.namePlaceholder')"
        >
      </div>
      <div class="form-group">
        <label>{{ $t('teamType.form.descriptionLabel') }}</label>
        <input
          v-model="form.description"
          type="text"
          class="form-control"
          maxlength="255"
        >
      </div>
      <div class="form-group">
        <label>
          <span class="required">*</span>
          {{ $t('teamType.form.statusLabel') }}
        </label>
        <select
          v-model.number="form.status"
          class="form-control"
        >
          <option :value="1">
            {{ $t('common.active') }}
          </option>
          <option :value="0">
            {{ $t('common.inactive') }}
          </option>
        </select>
      </div>
      <div class="text-right">
        <button
          type="button"
          class="btn btn-secondary mr-1"
          @click="mostrarForm = false"
        >
          {{ $t('common.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="salvando || !form.name.trim()"
          @click="salvar"
        >
          {{ $t('common.save') }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BSpinner, BBadge, BModal, BFormCheckbox, BButton,
} from 'bootstrap-vue'
import swal from 'sweetalert'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

/**
 * Tipos de equipe de um nó (cadastro da central, não da organização).
 * Num nó com filhos, inclui por padrão os tipos dos sub-nós, com a coluna do
 * nó dono — tipo novo é sempre criado no nó administrado.
 * Emite `changed` após salvar/excluir para a lista de equipes atualizar o
 * nome do tipo exibido.
 */
export default {
  name: 'TeamTypeIndex',
  components: {
    BSpinner, BBadge, BModal, BFormCheckbox, BButton,
  },
  mixins: [ApiErrorMixin],

  props: {
    nodeId: {
      type: [Number, String],
      required: true,
    },
    hasChildren: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tipos: [],
      incluirSubnos: this.hasChildren,
      carregando: false,
      salvando: false,
      mostrarForm: false,
      form: this.formVazio(),
    }
  },

  computed: {
    mostrarNo() {
      return this.hasChildren && this.incluirSubnos
    },
  },

  watch: {
    // A árvore de nós da tela pai pode chegar depois desta aba montar.
    hasChildren(tem) {
      this.incluirSubnos = tem
      this.carregar()
    },
  },

  mounted() {
    this.carregar()
  },

  methods: {
    formVazio() {
      return {
        id: null, name: '', description: '', status: 1,
      }
    },

    ordemNo(id) {
      return Number(id) === Number(this.nodeId) ? 0 : 1
    },

    // Tipo de sub-nó exige node.admin — mesma regra do TeamTypeService.
    podeGerenciar(tipo) {
      return this.$hasAbility('equipe.gerenciar')
        && (Number(tipo.node_id) === Number(this.nodeId) || this.$hasAbility('node.admin'))
    },

    async carregar() {
      this.carregando = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}team_types`, {
          params: { node_id: this.nodeId, paginated: false, with_descendants: this.mostrarNo ? 1 : 0 },
        })
        const tipos = res.data.data || []

        // Tipos do nó administrado primeiro, depois agrupados por sub-nó.
        this.tipos = this.mostrarNo
          ? tipos.slice().sort((a, b) => this.ordemNo(a.node_id) - this.ordemNo(b.node_id)
            || ((a.node && a.node.name) || '').localeCompare((b.node && b.node.name) || '')
            || (a.name || '').localeCompare(b.name || ''))
          : tipos
      } catch (e) {
        this.tipos = []
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('teamType.list.loadError')), 'error')
      } finally {
        this.carregando = false
      }
    },

    abrirNovo() {
      this.form = this.formVazio()
      this.mostrarForm = true
    },

    abrirEdicao(tipo) {
      this.form = {
        id: tipo.id,
        name: tipo.name,
        description: tipo.description || '',
        status: Number(tipo.status),
      }
      this.mostrarForm = true
    },

    async salvar() {
      this.salvando = true
      try {
        const base = `${this.$store.state.api}team_types`
        const corpo = {
          name: this.form.name.trim(),
          description: this.form.description || null,
          status: this.form.status,
          node_id: this.nodeId,
        }

        if (this.form.id) await this.$http.put(`${base}/${this.form.id}`, corpo)
        else await this.$http.post(base, corpo)

        this.mostrarForm = false
        this.$message(this.$t('teamType.messages.saveSuccessTitle'), this.$t('teamType.messages.saveSuccessMessage'), 'success')
        await this.carregar()
        this.$emit('changed')
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('teamType.list.saveError')), 'error')
      } finally {
        this.salvando = false
      }
    },

    async excluir(tipo) {
      const ok = await swal({
        title: this.$t('teamType.list.deleteConfirmTitle', { name: tipo.name }),
        text: this.$t('teamType.list.deleteConfirmText'),
        icon: 'warning',
        buttons: [this.$t('common.cancel'), this.$t('common.delete')],
        dangerMode: true,
      })
      if (!ok) return

      try {
        await this.$http.delete(`${this.$store.state.api}team_types/${tipo.id}`, {
          params: { node_id: this.nodeId },
        })
        this.$message(this.$t('teamType.messages.saveSuccessTitle'), this.$t('teamType.list.deletedMessage'), 'success')
        await this.carregar()
        this.$emit('changed')
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('teamType.list.deleteError')), 'error')
      }
    },
  },
}
</script>
